import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { UserCreateModal } from "@/components/users/UserCreateModal";
import { UserDrawer } from "@/components/users/UserDrawer";
import { UsersFilters, type UsersFiltersState } from "@/components/users/UsersFilters";
import { UsersTable, type UsersTableSort } from "@/components/users/UsersTable";
import { useUsersList } from "@/lib/hooks/users";
import type { UsersListFilters } from "@/lib/schemas/users";

const usersSearchSchema = z.object({
  // `highlight` is the deep-link target from the global search palette
  // (ADR-0091): clicking a user result navigates here with the user_id
  // so we can auto-open the drawer. Optional, free-form (UUID validated
  // by the API on the GET /users/{id} call).
  highlight: z.string().optional(),
  // `search` seed (backlog 2.3): truncate hint in the palette links here
  // with the current q, so the user lands on the list page with the
  // filter already applied + can browse the full match set.
  search: z.string().optional(),
});

export const Route = createFileRoute("/_app/users")({
  component: UsersPage,
  validateSearch: usersSearchSchema,
});

const DEFAULT_FILTERS: UsersFiltersState = {
  search: "",
  role: "all",
  status: "all",
  owner_scope: "all",
};

function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const { highlight, search: searchFromUrl } = Route.useSearch();
  const [filters, setFilters] = useState<UsersFiltersState>(() =>
    searchFromUrl
      ? { ...DEFAULT_FILTERS, search: searchFromUrl }
      : DEFAULT_FILTERS,
  );
  const [selectedId, setSelectedId] = useState<string | undefined>(highlight);
  const [createOpen, setCreateOpen] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  // The BE is forward-only (prev_cursor is always null), so "Indietro" is driven
  // by a client-side stack of the cursors of the pages we came from.
  const [backStack, setBackStack] = useState<(string | undefined)[]>([]);
  // header-click sort; default newest-first (mirror BE default)
  const [sort, setSort] = useState<UsersTableSort>({ field: "created_at", desc: true });

  const resetPaging = (): void => {
    setCursor(undefined);
    setBackStack([]);
  };

  // React to highlight URL changes (palette navigates with a different
  // user_id while we're already on /users): re-open the drawer.
  useEffect(() => {
    if (highlight) setSelectedId(highlight);
  }, [highlight]);

  // Same for ?search= (truncate-hint click from the palette).
  useEffect(() => {
    if (searchFromUrl) {
      setFilters((prev) => ({ ...prev, search: searchFromUrl }));
      resetPaging();
    }
  }, [searchFromUrl]);

  const apiFilters: UsersListFilters = useMemo(() => {
    const base: UsersListFilters = {
      owner_scope: filters.owner_scope,
      limit: 25,
    };
    if (filters.search) base.search = filters.search;
    if (filters.role !== "all") base.role = filters.role;
    if (filters.status !== "all") base.status = filters.status;
    base.sort = `${sort.desc ? "-" : ""}${sort.field}`;
    if (cursor) base.cursor = cursor;
    return base;
  }, [filters, sort, cursor]);

  const query = useUsersList(apiFilters);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("nav.users")}</h1>
          {query.data?.total_estimate !== undefined && (
            <p className="text-sm text-muted-foreground">
              {t("users.list.total", { count: query.data.total_estimate })}
            </p>
          )}
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 size-4" />
          {t("users.list.new_customer")}
        </Button>
      </header>

      <UsersFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          resetPaging();
        }}
      />

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("users.list.load_error")} — {query.error.message}
        </div>
      )}

      <UsersTable
        users={query.data?.data ?? []}
        isLoading={query.isLoading}
        selectedId={selectedId}
        onRowClick={(u) => setSelectedId(u.id)}
        sort={sort}
        onSort={(field) => {
          setSort((prev) =>
            prev.field === field
              ? { field, desc: !prev.desc }
              : { field, desc: field === "created_at" },
          );
          resetPaging(); // sort change → restart pagination
        }}
      />

      {query.data && (query.data.next_cursor || backStack.length > 0) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={backStack.length === 0}
            onClick={() => {
              setBackStack((s) => s.slice(0, -1));
              setCursor(backStack[backStack.length - 1]);
            }}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next_cursor}
            onClick={() => {
              setBackStack((s) => [...s, cursor]);
              setCursor(query.data.next_cursor ?? undefined);
            }}
          >
            {t("common.next")} →
          </Button>
        </div>
      )}

      <UserCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => setSelectedId(id)}
      />

      <UserDrawer userId={selectedId} onClose={() => setSelectedId(undefined)} />
    </div>
  );
}
